import { useState } from 'react';
import type { Residency } from '@salonomy/types';
import { Button, Card, Pill } from '@salonomy/ui';
import { useSalonResidencies } from '../../data/practitioner.js';
import { useLocations } from '../../data/locations.js';
import { useSalon } from '../../data/salon.js';
import { useAuth } from '../../lib/auth.js';
import { longDate } from '../../lib/time.js';
import { EndResidencyDialog } from './EndResidencyDialog.js';
import { InviteResidencyModal } from './InviteResidencyModal.js';
import { scheduleWeekdaySummary } from './schedule.js';

/**
 * WS11 — the salon-side residency roster (brief 11 §2). Lists the chair-rental residencies in the CURRENT
 * salon, invites an existing-account practitioner to take one, and ends a residency with a typed
 * confirmation. Owner/manager-only: the API gates /v1/residencies and the invite/end routes to managers,
 * and we mirror that with lib/auth's isManager so a non-manager never gets a bare 403 (they see an honest
 * note instead). Self-fetching (residencies / salon / locations) so the integrator mounts it with no props.
 *
 * DESIGN NOTE: built from the existing Settings card/token idiom (like WS6/WS7 surfaces). This is a real,
 * usable surface, NOT the design-gated flagship "My week" diary; visual gaps are flagged for a later
 * design round. Every figure shown (the schedule days, the lifecycle dates) is the server's data as-is —
 * nothing about availability or money is recomputed here.
 */
export function ResidenciesPanel() {
  const { isManager } = useAuth();

  // Non-managers see an honest note, not a 403 — the read stays disabled so we never fire a doomed request.
  const { data: salon } = useSalon();
  const { data: locations } = useLocations();
  const [includeEnded, setIncludeEnded] = useState(false);
  const { data: residencies = [], isLoading } = useSalonResidencies(includeEnded, isManager);

  const [inviteOpen, setInviteOpen] = useState(false);
  const [endTarget, setEndTarget] = useState<Residency | null>(null);

  if (!isManager) {
    return (
      <Card>
        <div className="section-title">Chair renters</div>
        <div className="muted" style={{ fontSize: 13, marginTop: 4 }}>
          Invite an independent professional who already has a Salonomy account to rent a chair here
          — they keep one login and their own client book. Only an owner or manager can manage
          residencies.
        </div>
      </Card>
    );
  }

  const tz = salon?.timezone ?? 'Europe/London';
  // Sort active first, then by salon-side join date (newest residencies on top within each group).
  const sorted = [...residencies].sort(
    (a, b) => Number(b.active) - Number(a.active) || b.createdAt.localeCompare(a.createdAt),
  );

  return (
    <Card>
      <div
        style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12 }}
      >
        <div>
          <div className="section-title">Chair renters</div>
          <div className="muted" style={{ fontSize: 13, marginTop: 4 }}>
            Independent professionals renting a chair here. Each keeps one Salonomy login across
            every salon they work in; ending a residency frees the seat without touching the salon’s
            history.
          </div>
        </div>
        <Button size="sm" onClick={() => setInviteOpen(true)}>
          Invite a chair renter
        </Button>
      </div>

      <div
        style={{
          display: 'flex',
          justifyContent: 'flex-end',
          marginTop: 10,
        }}
      >
        <Button size="sm" variant="ghost" onClick={() => setIncludeEnded((v) => !v)}>
          {includeEnded ? 'Hide ended' : 'Show ended'}
        </Button>
      </div>

      {isLoading ? (
        <div className="muted" style={{ fontSize: 13, padding: '12px 0 0' }}>
          Loading residencies…
        </div>
      ) : sorted.length === 0 ? (
        <div className="muted" style={{ fontSize: 13, padding: '12px 0 0' }}>
          No chair renters yet. Invite a practitioner who already has an account to rent a chair.
        </div>
      ) : (
        <div className="settings-list" style={{ marginTop: 8 }}>
          {sorted.map((r) => {
            const days = scheduleWeekdaySummary(r.schedule);
            return (
              <div key={r.staffId} className="settings-row">
                <div>
                  <div style={{ fontWeight: 600 }}>
                    {r.salonName}
                    {r.renterId ? (
                      <Pill tone="soft" className="mono">
                        Sealed book
                      </Pill>
                    ) : null}
                    {!r.active ? <Pill tone="danger">Ended</Pill> : null}
                  </div>
                  <div className="muted" style={{ fontSize: 12.5 }}>
                    {days ? `Works ${days}` : 'No schedule set yet'}
                  </div>
                  {!r.active && r.endedAt ? (
                    <div className="muted mono" style={{ fontSize: 11.5, marginTop: 2 }}>
                      ended {longDate(r.endedAt.slice(0, 10), tz)}
                    </div>
                  ) : null}
                </div>
                <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                  {r.active ? (
                    <Button size="sm" variant="ghost" onClick={() => setEndTarget(r)}>
                      End residency
                    </Button>
                  ) : (
                    <span
                      className="muted"
                      style={{ fontSize: 11.5 }}
                      title="Salon history preserved"
                    >
                      History preserved
                    </span>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {inviteOpen ? (
        <InviteResidencyModal
          locations={locations ?? []}
          clientBookMode={salon?.clientBookMode ?? 'shared'}
          onClose={() => setInviteOpen(false)}
        />
      ) : null}

      <EndResidencyDialog residency={endTarget} onClose={() => setEndTarget(null)} />
    </Card>
  );
}
